import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import { PERIODS, type Period } from './types'

export function usePeriod() {
  const [params, setParams] = useSearchParams()
  const raw = params.get('donem')
  const period: Period = (PERIODS as readonly string[]).includes(raw ?? '') ? (raw as Period) : '6'
  const setPeriod = (p: Period) =>
    setParams(
      (prev) => {
        const n = new URLSearchParams(prev)
        n.set('donem', p)
        return n
      },
      { replace: true },
    )
  return { period, months: Number(period), setPeriod }
}

type Row = Record<string, unknown>

function rowsOf(data: unknown): Row[] {
  if (Array.isArray(data)) return data as Row[]
  if (data && typeof data === 'object') {
    for (const key of ['items', 'data', 'stages', 'reps', 'columns', 'assignees']) {
      if (Array.isArray((data as Row)[key])) return (data as Row)[key] as Row[]
    }
  }
  return asList(data as Row[] | { items?: Row[] })
}
function str(v: unknown, fallback = '') {
  return v == null ? fallback : String(v)
}
function num(v: unknown) {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}
function range(months: number) {
  const to = new Date()
  const from = new Date(to)
  from.setMonth(from.getMonth() - months)
  const iso = (d: Date) => d.toISOString().slice(0, 10)
  return { from: iso(from), to: iso(to) }
}

export type FunnelStage = { code: string; name: string; position: number; accounts: number }
export type SalesFunnel = { stages: FunnelStage[]; winRatePercent: number; wonInPeriod: number; lostInPeriod: number }
export type RepRow = {
  userId: string
  name: string
  accountsOwned: number
  won: number
  lost: number
  open: number
  winRatePercent: number
  meetingsHeld: number
  demos: number
  demosConverted: number
  ordersApproved: number
  followupsDone: number
  followupsOverdue: number
}
export type DemoConversion = {
  total: number
  conversionRatePercent: number
  extended: number
  avgDaysToConvert: number
  medianDaysToConvert: number
  byStatus: { status: string; count: number }[]
}
export type TaskColumn = { board: string; columnKey: string; columnName: string; isDone: boolean; tasks: number; overdue: number; unassigned: number }
export type TaskAssignee = { userId: string; email: string; open: number; overdue: number; completed30d: number }
export type TasksReport = { columns: TaskColumn[]; assignees: TaskAssignee[] }
export type SetupDays = { count: number; avgDays: number; medianDays: number }
export type InstallationsReport = {
  licenseStale: number
  missingInBinary: number
  silentOver24h: number
  byEnvironmentStatus: { environment: string; status: string; installations: number }[]
  setupDays: { onPrem: SetupDays; cloud: SetupDays }
  accountInstallStages: { stage: string; accounts: number }[]
}

function mapFunnel(raw: unknown): SalesFunnel {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Row
  return {
    stages: rowsOf(row.stages ?? raw).map((s) => ({ code: str(s.code), name: str(s.name), position: num(s.position), accounts: num(s.accounts ?? s.count) })),
    winRatePercent: num(row.win_rate_percent ?? row.winRatePercent),
    wonInPeriod: num(row.won_in_period ?? row.wonInPeriod),
    lostInPeriod: num(row.lost_in_period ?? row.lostInPeriod),
  }
}
function mapReps(raw: unknown): RepRow[] {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Row
  return rowsOf(row.reps ?? raw).map((r) => ({
    userId: str(r.user_id ?? r.userId),
    name: str(r.name ?? r.email),
    accountsOwned: num(r.accounts_owned),
    won: num(r.won),
    lost: num(r.lost),
    open: num(r.open),
    winRatePercent: num(r.win_rate_percent),
    meetingsHeld: num(r.meetings_held),
    demos: num(r.demos),
    demosConverted: num(r.demos_converted),
    ordersApproved: num(r.orders_approved),
    followupsDone: num(r.followups_done),
    followupsOverdue: num(r.followups_overdue),
  }))
}
function mapDemos(raw: unknown): DemoConversion {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Row
  const by = (row.by_status ?? row.byStatus ?? {}) as Row
  return {
    total: num(row.total),
    conversionRatePercent: num(row.conversion_rate_percent),
    extended: num(row.extended),
    avgDaysToConvert: num(row.avg_days_to_convert),
    medianDaysToConvert: num(row.median_days_to_convert),
    byStatus: Object.entries(by).map(([status, count]) => ({ status, count: num(count) })),
  }
}
function mapTasks(raw: unknown): TasksReport {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Row
  return {
    columns: rowsOf(row.columns).map((c) => ({
      board: str(c.board),
      columnKey: str(c.column_key ?? c.columnKey),
      columnName: str(c.column_name ?? c.columnName),
      isDone: c.is_done === true,
      tasks: num(c.tasks),
      overdue: num(c.overdue),
      unassigned: num(c.unassigned),
    })),
    assignees: rowsOf(row.assignees).map((a) => ({
      userId: str(a.user_id ?? a.userId),
      email: str(a.email),
      open: num(a.open),
      overdue: num(a.overdue),
      completed30d: num(a.completed_30d ?? a.completed30d),
    })),
  }
}
function mapSetup(v: unknown): SetupDays {
  const row = (v && typeof v === 'object' ? v : {}) as Row
  return { count: num(row.count), avgDays: num(row.avg_days ?? row.avgDays), medianDays: num(row.median_days ?? row.medianDays) }
}
function mapInstallations(raw: unknown): InstallationsReport {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Row
  const setup = (row.setup_days ?? row.setupDays ?? {}) as Row
  return {
    licenseStale: num(row.license_stale),
    missingInBinary: num(row.missing_in_binary),
    silentOver24h: num(row.silent_over_24h),
    byEnvironmentStatus: rowsOf(row.by_environment_status ?? row.byEnvironmentStatus).map((r) => ({
      environment: str(r.environment),
      status: str(r.status),
      installations: num(r.installations),
    })),
    setupDays: { onPrem: mapSetup(setup.on_prem ?? setup.onPrem), cloud: mapSetup(setup.cloud) },
    accountInstallStages: rowsOf(row.account_install_stages ?? row.accountInstallStages).map((r) => ({ stage: str(r.stage), accounts: num(r.accounts) })),
  }
}

async function getReport<T>(path: string, months: number, map: (raw: unknown) => T) {
  return map(await api.get<unknown>(path, range(months)))
}

export const useSalesFunnel = (months: number) => useQuery({ queryKey: ['reports', 'sales-funnel', months], queryFn: () => getReport('/api/v1/reports/sales-funnel', months, mapFunnel) })
export const useRepPerformance = (months: number) => useQuery({ queryKey: ['reports', 'rep-performance', months], queryFn: () => getReport('/api/v1/reports/rep-performance', months, mapReps) })
export const useDemoConversion = (months: number) => useQuery({ queryKey: ['reports', 'demo-conversion', months], queryFn: () => getReport('/api/v1/reports/demo-conversion', months, mapDemos) })
export const useTasksReport = (months: number) => useQuery({ queryKey: ['reports', 'tasks', months], queryFn: () => getReport('/api/v1/reports/tasks', months, mapTasks) })
export const useInstallationsReport = (months: number) => useQuery({ queryKey: ['reports', 'installations', months], queryFn: () => getReport('/api/v1/reports/installations', months, mapInstallations) })

export function useOverviewReport(months: number) {
  return useQuery({
    queryKey: ['reports', 'overview', months],
    queryFn: async () => {
      const [funnel, reps, demos, tasks, installations] = await Promise.all([
        getReport('/api/v1/reports/sales-funnel', months, mapFunnel),
        getReport('/api/v1/reports/rep-performance', months, mapReps),
        getReport('/api/v1/reports/demo-conversion', months, mapDemos),
        getReport('/api/v1/reports/tasks', months, mapTasks),
        getReport('/api/v1/reports/installations', months, mapInstallations),
      ])
      return { funnel, reps, demos, tasks, installations }
    },
  })
}

export function useSalesReport(months: number) {
  return useQuery({
    queryKey: ['reports', 'sales', months],
    queryFn: async () => {
      const [funnel, reps] = await Promise.all([getReport('/api/v1/reports/sales-funnel', months, mapFunnel), getReport('/api/v1/reports/rep-performance', months, mapReps)])
      return { funnel, reps }
    },
  })
}

export function useOperationsReport(months: number) {
  return useQuery({
    queryKey: ['reports', 'operations', months],
    queryFn: async () => ({
      tasks: await getReport('/api/v1/reports/tasks', months, mapTasks),
      installations: await getReport('/api/v1/reports/installations', months, mapInstallations),
      demos: await getReport('/api/v1/reports/demo-conversion', months, mapDemos),
    }),
  })
}

/* ---- Kârlılık (proje / müşteri / gelir-gider kaynakları) ---- */

export type ProjectProfit = {
  id: string
  name: string
  code: string
  customers: string[]
  contractValue: number
  budget: number
  income: number
  expense: number
  salesExpense: number
  totalCost: number
  profit: number
  margin: number | null
  invoiced: number
  collected: number
  budgetUsed: number | null
}
export type CustomerSales = {
  id: string
  title: string
  quotesCount: number
  quotesTotal: number
  ordersCount: number
  ordersTotal: number
  invoiced: number
  collected: number
  outstanding: number
  income: number
  expense: number
  salesExpense: number
  profit: number
  margin: number | null
}
export type ProfitabilityReport = {
  totals: { income: number; expense: number; salesExpense: number; profit: number; margin: number | null; ordersTotal: number; invoiced: number; collected: number; outstanding: number }
  projects: ProjectProfit[]
  customers: CustomerSales[]
  categories: { kind: 'income' | 'expense'; id: string; name: string; amount: number; count: number; share: number }[]
  months: { month: string; income: number; expense: number; net: number }[]
}

const nn = (v: unknown) => (v == null || v === '' ? null : num(v))

function mapProfitability(raw: unknown): ProfitabilityReport {
  const r = (raw ?? {}) as Row
  const t = (r.totals ?? {}) as Row
  return {
    totals: {
      income: num(t.income),
      expense: num(t.expense),
      salesExpense: num(t.sales_expense),
      profit: num(t.profit),
      margin: nn(t.margin_percent),
      ordersTotal: num(t.orders_total),
      invoiced: num(t.invoiced),
      collected: num(t.collected),
      outstanding: num(t.outstanding),
    },
    projects: rowsOf(r.projects).map((p) => ({
      id: str(p.project_id),
      name: str(p.name),
      code: str(p.code),
      customers: Array.isArray(p.customers) ? p.customers.map((c) => str(c)) : [],
      contractValue: num(p.contract_value),
      budget: num(p.budget),
      income: num(p.income),
      expense: num(p.expense),
      salesExpense: num(p.sales_expense),
      totalCost: num(p.total_cost),
      profit: num(p.profit),
      margin: nn(p.margin_percent),
      invoiced: num(p.invoiced),
      collected: num(p.collected),
      budgetUsed: nn(p.budget_used_percent),
    })),
    customers: rowsOf(r.customers).map((c) => ({
      id: str(c.account_id),
      title: str(c.title),
      quotesCount: num(c.quotes_count),
      quotesTotal: num(c.quotes_total),
      ordersCount: num(c.orders_count),
      ordersTotal: num(c.orders_total),
      invoiced: num(c.invoiced),
      collected: num(c.collected),
      outstanding: num(c.outstanding),
      income: num(c.income),
      expense: num(c.expense),
      salesExpense: num(c.sales_expense),
      profit: num(c.profit),
      margin: nn(c.margin_percent),
    })),
    categories: rowsOf(r.categories).map((c) => ({
      kind: c.kind === 'income' ? 'income' : 'expense',
      id: str(c.category_id),
      name: str(c.name),
      amount: num(c.amount),
      count: num(c.count),
      share: num(c.share_percent),
    })),
    months: rowsOf(r.months).map((m) => ({ month: str(m.month), income: num(m.income), expense: num(m.expense), net: num(m.net) })),
  }
}

export const useProfitabilityReport = (months: number) =>
  useQuery({ queryKey: ['reports', 'profitability', months], queryFn: () => getReport('/api/v1/reports/profitability', months, mapProfitability) })
